import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug, generateRRule, getTimezoneOffset } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE, TIMEZONE_MAP } from '@/common/constants';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { User } from '@/common/nylas-api-request';
import { ThemeConfig } from '@nylas/core';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const periodMap = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
  year: 'yearly',
};

type RepeatEndType = 'never' | 'on' | 'after';
type RecurrenceType = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

/**
 * The `nylas-event-timeslot` component is a UI component that allows users to select the event type for the scheduler configuration.
 */
@Component({
  tag: 'nylas-event-timeslot',
  styleUrl: 'nylas-event-timeslot.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasEventTimeslot {
  @Element() host!: HTMLNylasEventTimeslotElement;

  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'event-timeslot';
  /**
   * The initial value of the event timeslot.
   */
  @Prop() initialValue?: {
    when: {
      start_time: number;
      end_time: number;
      start_timezone: string;
      end_timezone: string;
    };
    recurrence: string[];
  };

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The current user.
   */
  @Prop() currentUser?: User;
  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The selected date.
   */
  @State() selectedDate!: string;

  /**
   * The selected start time.
   */
  @State() selectedStartTime!: string;

  /**
   * The selected end time.
   */
  @State() selectedEndTime!: string;

  /**
   * The timezone.
   */
  @State() selectedTimezone!: string;

  /**
   * The selected recurrence type.
   */
  @State() selectedRecurrenceType!: RecurrenceType;

  /**
   * Time range error.
   */
  @State() timeRangeError: boolean = false;

  /**
   * The selected rrule.
   */
  @State() selectedRrule!: string | null;

  /**
   * Recurrence options.
   */
  @State() recurrenceOptions: { label: string; value: string }[] = [];
  /**
   * Repeat every frequency.
   */
  @State() repeatEveryInterval: number = 1;
  /**
   * Repeat every period.
   */
  @State() repeatEveryFrequency: string = 'week';
  /**
   * Repeat on days.
   */
  @State() repeatOnDays: string[] = [];
  /**
   * Repeat monthly on.
   */
  @State() repeatMonthlySameDate: boolean = true;
  /**
   * Repeat end type.
   */
  @State() repeatEndType: RepeatEndType = 'never';
  /**
   * Repeat end after.
   */
  @State() repeatEndAfter: number = 1;
  /**
   * Repeat end on (custom recurrence).
   */
  @State() repeatEndOn: string | undefined;
  /**
   * Month recurrence options.
   */
  @State() monthRecurrenceOptions: { label: string; value: string }[] = [];
  /**
   * The end date for recurrence (non-custom recurrence).
   */
  @State() endDate: string | undefined;

  /**
   * The flag to indicate if the date input is in error state.
   */
  @State() hasDateTimeInputError: boolean = false;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * This event is fired when the value of the event location changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * This event is fired when the value of the event timeslot changes.
   */
  @Event() timeWindowFormError!: EventEmitter<{
    key: string;
    message: string;
    uniqueIdentifier?: string;
  }>;

  /**
   * This event is fired when the value of the event timeslot changes.
   */
  @Event() dateTimeInputError!: EventEmitter<{
    key: string;
    message: string;
  }>;

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-event-timeslot', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-event-timeslot', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-event-timeslot', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-event-timeslot', 'componentDidLoad');

    // Set defaults from initialValue or fallback to current values
    if (this.initialValue) {
      const { when, recurrence } = this.initialValue;

      if (when) {
        this.selectedDate = dayjs.unix(when.start_time).tz(when.start_timezone).format('YYYY-MM-DD');
        this.selectedStartTime = dayjs.unix(when.start_time).tz(when.start_timezone).format('hh:mma');
        this.selectedEndTime = dayjs.unix(when.end_time).tz(when.end_timezone).format('hh:mma');
        this.selectedTimezone = when.start_timezone;
      } else {
        this.selectedDate = new Date().toISOString().split('T')[0];
        const defaultTimes = this.getSuggestedTime();
        this.selectedStartTime = defaultTimes.startTime;
        this.selectedEndTime = defaultTimes.endTime;
        this.selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      if (recurrence && recurrence.length > 0) {
        this.selectedRrule = recurrence[0];
        const repeatEndAfter = recurrence[0]?.split(';')?.find(s => s.includes('COUNT'));
        if (repeatEndAfter) {
          this.repeatEndAfter = parseInt(repeatEndAfter.split('=')[1]);
        }
        const daysString = recurrence[0]?.split(';')?.find(s => s.includes('BYDAY'));
        if (daysString) {
          this.repeatOnDays = daysString.split('=')[1].split(',');
        }
        const until = recurrence[0]?.split(';')?.find(s => s.includes('UNTIL'));
        if (until) {
          const untilDate = dayjs(until.split('=')[1], 'YYYYMMDDTHHmmss').toDate();
          this.repeatEndOn = dayjs(untilDate).toISOString().split('T')[0];
          this.repeatEndType = 'on';
        }
        const recurrenceType = this.getRecurrenceTypeFromRrule(recurrence[0]);
        this.selectedRecurrenceType = recurrenceType || 'none';
      } else {
        this.selectedRecurrenceType = 'none';
        this.selectedRrule = generateRRule(this.selectedDate, this.selectedRecurrenceType, undefined, undefined, this.selectedTimezone);
      }
    } else {
      this.selectedDate = new Date().toISOString().split('T')[0];
      const defaultTimes = this.getSuggestedTime();
      this.selectedStartTime = defaultTimes.startTime;
      this.selectedEndTime = defaultTimes.endTime;
      this.selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.selectedRecurrenceType = 'none';
      this.selectedRrule = generateRRule(this.selectedDate, this.selectedRecurrenceType, undefined, undefined, this.selectedTimezone);
    }

    this.recurrenceOptions = this.generateRecurrenceOptions(this.selectedDate, this.selectedTimezone);
    this.monthRecurrenceOptions = this.getMonthRecurrenceOptions(this.selectedDate, this.selectedTimezone);

    // Trigger initial value change
    this.handleValueChanged(true);
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-event-timeslot', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  // Utility function to get the ordinal suffix (e.g., 1st, 2nd, 3rd)
  getOrdinalSuffix(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  getRecurrenceTypeFromRrule(rrule: string): RecurrenceType | undefined {
    if (rrule.includes('INTERVAL')) return 'custom';
    if (rrule.includes('FREQ=DAILY')) return 'daily';
    if (rrule.includes('FREQ=WEEKLY')) return 'weekly';
    if (rrule.includes('FREQ=MONTHLY')) return 'monthly';
    if (rrule.includes('FREQ=YEARLY')) return 'yearly';
    return undefined;
  }

  // Function to generate dynamic recurrence options
  generateRecurrenceOptions(selectedDate: string, timezoneStr: string) {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Parse the date in the given timezone
    const selectedDay = dayjs.tz(selectedDate, timezoneStr);

    const dayOfWeek = daysOfWeek[selectedDay.day()]; // Get the day of the week (e.g., Thursday)

    // Determine the occurrence of the day in the month (e.g., 1st, 2nd, etc.)
    const date = selectedDay.date();
    const occurrenceInMonth = Math.ceil(date / 7); // e.g., 1st Thursday

    // Generate options
    return [
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.none'), value: 'none' },
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.daily'), value: 'daily' },
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.weekly', { dayOfWeek: dayOfWeek }), value: 'weekly' },
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.monthly', { dayOfMonth: `${this.getOrdinalSuffix(occurrenceInMonth)} ${dayOfWeek}` }), value: 'monthly' },
      {
        label: i18next.t('nylasEventTimeslot.recurrenceOptions.yearly', { dateOfMonth: `${selectedDay.format('MMMM D')}` }),
        value: 'yearly',
      },
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.custom'), value: 'custom' },
    ];
  }

  // Function to generate dynamic month recurrence options
  getMonthRecurrenceOptions(selectedDate: string, timezoneStr: string) {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const selectedDay = dayjs.tz(selectedDate, timezoneStr);
    const dayOfWeek = daysOfWeek[selectedDay.day()]; // Get the day of the week (e.g., Thursday)
    const date = selectedDay.date();
    const occurrenceInMonth = Math.ceil(date / 7);

    return [
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.monthOnDay', { date: date }), value: 'monthly-same-date' },
      { label: i18next.t('nylasEventTimeslot.recurrenceOptions.monthly', { dayOfMonth: `${this.getOrdinalSuffix(occurrenceInMonth)} ${dayOfWeek}` }), value: 'monthly' },
    ];
  }

  // Function to get the default suggested time shown in the time range picker
  getSuggestedTime() {
    const now = dayjs();

    // Round current time to the next 30-minute increment
    const minutes = now.minute();
    const roundedMinutes = minutes <= 30 ? 30 : 60;
    const startTime = now.set('minute', roundedMinutes).set('second', 0);

    // Calculate end time (30 minutes after start time)
    const endTime = startTime.add(30, 'minute');

    // Format the times in "hh:mma" format
    return {
      startTime: startTime.format('hh:mma'), // e.g., "03:00pm"
      endTime: endTime.format('hh:mma'), // e.g., "03:30pm"
    };
  }

  getTimezoneLabelHTML(timezone: string) {
    const offset = getTimezoneOffset(timezone);
    return (
      <span
        class="timezone-label"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '-webkit-fill-available',
        }}
      >
        {TIMEZONE_MAP[timezone]}{' '}
        <span
          class="timezone-offset"
          style={{
            fontSize: '14px',
            fontWeight: '400',
            color: 'var(--nylas-base-500)',
          }}
        >
          {offset}
        </span>
      </span>
    );
  }

  // Function to check if the start time is after the end time (used for validation)
  isStartTimeAfterEndTime(startTime: string, endTime: string): boolean {
    // Parse the times using Day.js
    const start = dayjs(startTime, 'hh:mma');
    const end = dayjs(endTime, 'hh:mma');

    // Compare start and end times
    return start.isAfter(end);
  }

  isStartTimeEqualToEndTime(startTime: string, endTime: string): boolean {
    const start = dayjs(startTime, 'hh:mma');
    const end = dayjs(endTime, 'hh:mma');
    return start.isSame(end);
  }

  isStartTimeBeforeCurrentDate(date: string, startTime: string, timezone: string): boolean {
    const dateTime = dayjs.tz(`${date} ${startTime}`, 'YYYY-MM-DD hh:mma', timezone);
    return dateTime.isBefore(dayjs());
  }

  validateDateTimeInput(date: string, startTime: string, timezone: string): { isValid: boolean; errorMessage: string } {
    const dateTimeWithStartTime = dayjs.tz(`${date} ${startTime}`, 'YYYY-MM-DD hh:mma', timezone);
    const isInPast = dateTimeWithStartTime.isBefore(dayjs());

    return {
      isValid: !isInPast,
      errorMessage: isInPast ? i18next.t('nylasEventTimeslot.errors.startTimeBeforeCurrentDate') : '',
    };
  }

  // Function to get the start and end times in Unix timestamp format (used in the valueChanged event)
  getStartAndEndTimes() {
    const start_time = dayjs.tz(`${this.selectedDate} ${this.selectedStartTime}`, 'YYYY-MM-DD hh:mma', this.selectedTimezone).toDate();
    const end_time = dayjs.tz(`${this.selectedDate} ${this.selectedEndTime}`, 'YYYY-MM-DD hh:mma', this.selectedTimezone).toDate();

    return {
      start_time: Math.round(start_time.getTime() / 1000),
      end_time: Math.round(end_time.getTime() / 1000),
    };
  }

  // Function to get the custom options for the RRule
  getCustomOptions() {
    if (this.selectedRecurrenceType !== 'custom') {
      return undefined;
    }

    const repeatFreq = periodMap[this.repeatEveryFrequency];

    return {
      frequency: repeatFreq,
      interval: this.repeatEveryInterval,
      daysOfWeek: this.repeatEveryFrequency === 'week' ? this.repeatOnDays : undefined,
      monthlySameDate: this.repeatEveryFrequency === 'month' ? this.repeatMonthlySameDate : undefined,
      end: {
        type: this.repeatEndType,
        count: this.repeatEndType === 'after' ? this.repeatEndAfter : undefined,
        date: this.repeatEndType === 'on' ? this.repeatEndOn : undefined,
      },
    };
  }

  handleValueChanged(noValue: boolean = false) {
    const customOptions = this.getCustomOptions();
    this.selectedRrule = generateRRule(this.selectedDate, this.selectedRecurrenceType, this.repeatEndOn, customOptions, this.selectedTimezone);
    const { start_time, end_time } = this.getStartAndEndTimes();
    const internalsObj = {
      when: {
        start_time: start_time,
        end_time: end_time,
        start_timezone: this.selectedTimezone,
        end_timezone: this.selectedTimezone,
      },
      recurrence: this.selectedRrule ? [this.selectedRrule] : [],
    };
    this.internals.setFormValue(JSON.stringify(internalsObj), this.name);
    if (!noValue) {
      this.valueChanged.emit({
        value: JSON.stringify(internalsObj),
        name: this.name,
      });
    }
  }

  // Listeners
  @Listen('timeChange')
  setTime(event: CustomEvent<{ key: string; value: string }>) {
    const { key, value } = event.detail;
    if (key === 'event-timeslot-start-time') {
      this.selectedStartTime = value;
    } else if (key === 'event-timeslot-end-time') {
      this.selectedEndTime = value;
    }
    const validateStartTimeAfterEndTime = this.isStartTimeAfterEndTime(this.selectedStartTime, this.selectedEndTime);
    const validateStartTimeEqualToEndTime = this.isStartTimeEqualToEndTime(this.selectedStartTime, this.selectedEndTime);

    // Update the validation to use the validation method
    const validation = this.validateDateTimeInput(this.selectedDate, this.selectedStartTime, this.selectedTimezone);
    const validateStartTimeBeforeCurrentDate = !validation.isValid;

    this.timeRangeError = validateStartTimeAfterEndTime || validateStartTimeEqualToEndTime || validateStartTimeBeforeCurrentDate;
    let error = '';
    if (validateStartTimeAfterEndTime) {
      error = i18next.t('nylasEventTimeslot.errors.invalidTimeRange');
    } else if (validateStartTimeEqualToEndTime) {
      error = i18next.t('nylasEventTimeslot.errors.startTimeSameAsEndTime');
    } else if (validateStartTimeBeforeCurrentDate) {
      error = validation.errorMessage;
    } else {
      error = '';
    }
    this.timeWindowFormError.emit({
      key: this.name,
      message: error,
      uniqueIdentifier: 'time-range',
    });
    if (error) {
      return;
    }

    this.handleValueChanged();
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(
    event: CustomEvent<{
      value: RecurrenceType | RepeatEndType | string;
      name: string;
    }>,
  ) {
    const { name, value } = event.detail;
    if (name === 'timezone') {
      this.selectedTimezone = value;
      const date = dayjs(this.selectedDate);
      const validation = this.validateDateTimeInput(date.format('YYYY-MM-DD'), this.selectedStartTime, this.selectedTimezone);

      this.hasDateTimeInputError = !validation.isValid;
      this.dateTimeInputError.emit({
        key: this.name,
        message: validation.errorMessage,
      });

      if (!validation.isValid) {
        return;
      }
    } else if (name === 'recurrence-type') {
      this.selectedRecurrenceType = value as RecurrenceType;
    } else if (name === 'month-recurrence-type') {
      this.repeatMonthlySameDate = value === 'monthly-same-date';
    } else if (name === 'custom-recurrence-end-type') {
      this.repeatEndType = value as RepeatEndType;
    }

    this.handleValueChanged();
  }

  @Listen('nylasFormInputChanged')
  nylasFormInputChangedHandler(event: CustomEvent<{ value: string; name: string; error?: string }>) {
    const { name, value } = event.detail;
    if (name === 'date-time-input') {
      const date = dayjs(value);
      const validation = this.validateDateTimeInput(date.format('YYYY-MM-DD'), this.selectedStartTime, this.selectedTimezone);

      this.hasDateTimeInputError = !validation.isValid;
      this.dateTimeInputError.emit({
        key: this.name,
        message: validation.errorMessage,
      });

      if (!validation.isValid) {
        return;
      }

      this.selectedDate = date.format('YYYY-MM-DD');
      this.recurrenceOptions = this.generateRecurrenceOptions(this.selectedDate, this.selectedTimezone);
      this.monthRecurrenceOptions = this.getMonthRecurrenceOptions(this.selectedDate, this.selectedTimezone);
    } else if (name === 'recurrence-end-date' || name === 'custom-recurrence-end-date') {
      const error = event.detail?.error || '';
      this.timeWindowFormError.emit({
        key: this.name,
        message: error,
        uniqueIdentifier: name,
      });
      this.repeatEndOn = value;
    } else if (name === 'recurrence-end-after') {
      const parsedValue = parseInt(value);
      if (parsedValue < 1 || isNaN(parsedValue)) {
        this.timeWindowFormError.emit({
          key: this.name,
          message: i18next.t('fixFormErrors'),
        });
        return;
      }
      this.repeatEndAfter = parsedValue;
    }

    this.handleValueChanged();
  }

  @Listen('timePeriodChanged')
  timePeriodChangedHandler(event: CustomEvent<{ number: number; period: 'day' | 'week' | 'month' | 'year' }>) {
    const { number, period } = event.detail;
    this.repeatEveryInterval = number;
    this.repeatEveryFrequency = period;

    if (period === 'month') {
      this.monthRecurrenceOptions = this.getMonthRecurrenceOptions(this.selectedDate, this.selectedTimezone);
    }

    this.handleValueChanged();
  }

  @RegisterComponent<NylasEventTimeslot, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-event-timeslot',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.currentUser', 'currentUser'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      valueChanged: async (event: CustomEvent<{ value: string; name: string }>, _nylasConnector: NylasSchedulerConfigConnector) => {
        debug('nylas-event-timeslot', 'valueChanged', event.detail);
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const icloudProvider = this.currentUser?.provider === 'icloud';
    const defaultRecurrenceOption = this.recurrenceOptions.find(option => option.value === this.selectedRecurrenceType);
    const timezoneOptions = Object.keys(TIMEZONE_MAP).map(key => ({
      labelHTML: this.getTimezoneLabelHTML(key),
      label: TIMEZONE_MAP[key],
      value: key,
    }));
    const selectedTimezoneOption = timezoneOptions.find(i => i.value === this.selectedTimezone);
    const endOptions = [
      { label: i18next.t('nylasEventTimeslot.customRecurrence.endsOptions.never'), value: 'never' },
      { label: i18next.t('nylasEventTimeslot.customRecurrence.endsOptions.on'), value: 'on' },
      { label: i18next.t('nylasEventTimeslot.customRecurrence.endsOptions.after'), value: 'after' },
    ];
    const defaultEndOnDate = dayjs(this.selectedDate).add(1, 'year').toISOString().split('T')[0];
    const defaultEndAfter = this.repeatEveryFrequency === 'week' ? 4 : this.repeatEveryFrequency === 'month' ? 12 : 5;
    this.repeatEndOn = this.repeatEndOn || defaultEndOnDate;
    this.repeatEndAfter = this.repeatEndAfter || defaultEndAfter;
    const today = dayjs().tz(this.selectedTimezone);
    return (
      <Host>
        <div class="nylas-event-timeslot" part="netimeslot">
          <label htmlFor="date-time-input">{i18next.t('nylasEventTimeslot.dateTimeLabel')}</label>
          <div class="date-time-input" part="netimeslot__date-time-input">
            <div class="date-time-picker" part="netimeslot__date-time-picker">
              <nylas-date-component
                minDate={dayjs().tz(this.selectedTimezone).toISOString().split('T')[0]}
                name="date-time-input"
                defaultValue={this.selectedDate}
                id={'date-time-input'}
                class={{ error: this.hasDateTimeInputError }}
                required={true}
                hasError={this.hasDateTimeInputError}
                themeConfig={this.themeConfig}
              ></nylas-date-component>
              <div class="time-range" part="netimeslot__time-range">
                <nylas-time-window-picker
                  id={'event-timeslot-start-time'}
                  hasError={this.timeRangeError}
                  time={this.selectedStartTime}
                  language={this.selectedLanguage}
                  name={'event-timeslot-start-time'}
                  key={'event-timeslot-start-time'}
                  themeConfig={this.themeConfig}
                  exportparts="time-picker: nap__time-picker-container, time-input: nap__time-picker-input, times: nap__time-picker-times"
                />
                <span>&ndash;</span>
                <nylas-time-window-picker
                  id={'event-timeslot-end-time'}
                  hasError={this.timeRangeError}
                  time={this.selectedEndTime}
                  language={this.selectedLanguage}
                  name={'event-timeslot-end-time'}
                  key={'event-timeslot-end-time'}
                  minimumStartTime={this.selectedStartTime}
                  themeConfig={this.themeConfig}
                  exportparts="time-picker: nap__time-picker-container, time-input: nap__time-picker-input, times: nap__time-picker-times"
                />
              </div>
              {selectedTimezoneOption?.label && (
                <select-dropdown
                  searchPlaceholder={i18next.t('search')}
                  name="timezone"
                  exportparts="sd_dropdown: nap__timezone-container, sd_dropdown-button: nap__timezone-button, sd_dropdown-content: nap__timezone-dropdown-content"
                  options={timezoneOptions}
                  withChevron={false}
                  defaultSelectedOption={selectedTimezoneOption}
                  themeConfig={this.themeConfig}
                >
                  <span slot="select-icon">
                    <globe-icon width="20" height="20" />
                  </span>
                </select-dropdown>
              )}
            </div>
            {!icloudProvider && (
              // iCloud provider does not support recurrence options
              <div class="recurrence-options" part="netimeslot__recurrence-options">
                {defaultRecurrenceOption && defaultRecurrenceOption?.label && (
                  <div class="recurrence-type" part="netimeslot__recurrence-type">
                    <label htmlFor="recurrence-type">{i18next.t('nylasEventTimeslot.recurrenceLabel')}</label>
                    <select-dropdown
                      withSearch={false}
                      id="recurrence-type"
                      name="recurrence-type"
                      options={this.recurrenceOptions}
                      themeConfig={this.themeConfig}
                      defaultSelectedOption={defaultRecurrenceOption}
                      exportparts="sd_dropdown: netimeslot__dropdown, sd_dropdown-button: netimeslot__dropdown-button, sd_dropdown-content: netimeslot__dropdown-content"
                    ></select-dropdown>
                  </div>
                )}
                {this.selectedRecurrenceType !== 'custom' && this.selectedRecurrenceType !== 'none' && (
                  <div class="recurrence-end" part="netimeslot__recurrence-end">
                    <label>{i18next.t('nylasEventTimeslot.endDate')}</label>
                    <nylas-date-component
                      name="recurrence-end-date"
                      minDate={today.toISOString().split('T')[0]}
                      defaultValue={this.repeatEndOn || defaultEndOnDate}
                      id={'recurrence-end-date'}
                      required={true}
                      themeConfig={this.themeConfig}
                    ></nylas-date-component>
                  </div>
                )}

                {this.selectedRecurrenceType === 'custom' && (
                  <div class="custom-recurrence-time-period" part="netimeslot__custom-recurrence">
                    <label htmlFor="">{i18next.t('nylasEventTimeslot.customRecurrence.repeatEvery')}</label>
                    <time-period-selector
                      id="custom-recurrence-time-period"
                      exportparts="tps__number-dropdown: netimeslot__number-dropdown, tps__number-dropdown-button: netimeslot__number-dropdown-button, tps__number-dropdown-content: netimeslot__number-dropdown-content, tps__period-dropdown: netimeslot__period-dropdown, tps__period-dropdown-button: netimeslot__period-dropdown-button, tps__period-dropdown-content: netimeslot__period-dropdown-content"
                      timePeriods={[
                        { value: 'day', label: i18next.t('timePeriod', { context: 'day' }) },
                        { value: 'week', label: i18next.t('timePeriod', { context: 'week' }) },
                        { value: 'month', label: i18next.t('timePeriod', { context: 'month' }) },
                        { value: 'year', label: i18next.t('timePeriod', { context: 'year' }) },
                      ]}
                      themeConfig={this.themeConfig}
                      defaultSelectedNumber={this.repeatEveryInterval}
                      defaultSelectedPeriod={this.repeatEveryFrequency}
                    />
                  </div>
                )}
                <div class="repeat-on">
                  {this.selectedRecurrenceType === 'custom' && this.repeatEveryFrequency === 'week' && (
                    <div class="days">
                      <label>{i18next.t('nylasEventTimeslot.customRecurrence.repeatOn')}</label>
                      <div class="buttons">
                        {[
                          { label: 'S', value: 'SU', title: 'Sunday' },
                          { label: 'M', value: 'MO', title: 'Monday' },
                          { label: 'T', value: 'TU', title: 'Tuesday' },
                          { label: 'W', value: 'WE', title: 'Wednesday' },
                          { label: 'T', value: 'TH', title: 'Thursday' },
                          { label: 'F', value: 'FR', title: 'Friday' },
                          { label: 'S', value: 'SA', title: 'Saturday' },
                        ].map(day => (
                          <button
                            class={{ selected: this.repeatOnDays.includes(day.value) }}
                            title={day.title}
                            onClick={() => {
                              this.repeatOnDays = this.repeatOnDays.includes(day.value) ? this.repeatOnDays.filter(d => d !== day.value) : [...this.repeatOnDays, day.value];
                              this.handleValueChanged();
                            }}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {this.selectedRecurrenceType === 'custom' && this.repeatEveryFrequency === 'month' && (
                    <div class="month-recurrence">
                      <span></span>
                      <select-dropdown
                        withSearch={false}
                        id="month-recurrence-type"
                        name="month-recurrence-type"
                        options={this.monthRecurrenceOptions}
                        themeConfig={this.themeConfig}
                        defaultSelectedOption={this.repeatMonthlySameDate ? this.monthRecurrenceOptions[0] : this.monthRecurrenceOptions[1]}
                        exportparts="sd_dropdown: netimeslot__dropdown, sd_dropdown-button: netimeslot__dropdown-button, sd_dropdown-content: netimeslot__dropdown-content"
                      ></select-dropdown>
                    </div>
                  )}
                  <div class="custom-recurrence">
                    {this.selectedRecurrenceType === 'custom' && (
                      <div class="custom-recurrence-ends">
                        <label htmlFor="recurrence-type">{i18next.t('nylasEventTimeslot.customRecurrence.ends')}</label>
                        <select-dropdown
                          withSearch={false}
                          id="custom-recurrence-end-type"
                          name="custom-recurrence-end-type"
                          options={endOptions}
                          themeConfig={this.themeConfig}
                          defaultSelectedOption={endOptions.find(option => option.value === this.repeatEndType)}
                          exportparts="sd_dropdown: netimeslot__dropdown, sd_dropdown-button: netimeslot__dropdown-button, sd_dropdown-content: netimeslot__dropdown-content"
                        ></select-dropdown>
                      </div>
                    )}
                    {this.selectedRecurrenceType === 'custom' && this.repeatEndType === 'on' && (
                      <div>
                        <nylas-date-component
                          name="custom-recurrence-end-date"
                          minDate={today.toISOString().split('T')[0]}
                          defaultValue={this.repeatEndOn || defaultEndOnDate}
                          id={'custom-recurrence-end-date'}
                          required={true}
                          themeConfig={this.themeConfig}
                        ></nylas-date-component>
                      </div>
                    )}
                    {this.selectedRecurrenceType === 'custom' && this.repeatEndType === 'after' && (
                      <div class="custom-recurrence-end-after">
                        <input-component
                          name="recurrence-end-after"
                          id="recurrence-end-after"
                          type="number"
                          minValue={1}
                          themeConfig={this.themeConfig}
                          defaultValue={defaultEndAfter.toString()}
                          exportparts="ic_input: netimeslot__input-textfield"
                        />
                        <span>{i18next.t('nylasEventTimeslot.customRecurrence.occurrences')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
